import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Truck, Home, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1F7A63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex flex-col items-center justify-center px-4">
        <Package className="w-24 h-24 text-[#6B4F2A]/30 mb-6" />
        <h1 className="text-2xl font-bold text-[#2C1E0F] mb-4">Order Not Found</h1>
        <Link to="/shop" className="btn-primary">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="order-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1F7A63] rounded-full mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight mb-4" data-testid="order-success-title">
            Order Placed Successfully!
          </h1>
          <p className="text-[#574021] text-lg">
            Thank you for your order. We'll send you a confirmation email shortly.
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 overflow-hidden mb-8">
          {/* Order Header */}
          <div className="bg-[#1F7A63] text-white p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-white/70 text-sm">Order Number</p>
                <p className="font-bold text-xl" data-testid="order-number">{order.order_number}</p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-white/70 text-sm">Order Status</p>
                <p className="font-semibold capitalize" data-testid="order-status">{order.status}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="p-6 border-b border-[#6B4F2A]/10">
            <h3 className="font-bold text-[#2C1E0F] mb-4">Order Items</h3>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex gap-4" data-testid={`order-item-${index}`}>
                  <img
                    src={item.image}
                    alt={item.product_name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-[#2C1E0F]">{item.product_name}</p>
                    <p className="text-sm text-[#574021]">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium text-[#1F7A63]">
                    Rs. {(item.price * item.quantity).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Info */}
          <div className="p-6 border-b border-[#6B4F2A]/10">
            <h3 className="font-bold text-[#2C1E0F] mb-4">Shipping Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[#574021]">
              <div className="flex items-start gap-3">
                <Home className="w-5 h-5 text-[#1F7A63] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#2C1E0F]">{order.shipping_info.full_name}</p>
                  <p>{order.shipping_info.address}</p>
                  <p>{order.shipping_info.city}, {order.shipping_info.postal_code}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#1F7A63]" />
                  <span>{order.shipping_info.phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#1F7A63]" />
                  <span>{order.shipping_info.email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment & Total */}
          <div className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm text-[#574021]">Payment Method</p>
                <p className="font-medium text-[#2C1E0F] capitalize" data-testid="order-payment-method">
                  {order.payment_method === 'cod' ? 'Cash on Delivery' : 'JazzCash'}
                </p>
              </div>
              <div className="space-y-1 text-right">
                <div className="flex justify-between md:justify-end gap-8 text-[#574021]">
                  <span>Subtotal:</span>
                  <span>Rs. {order.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between md:justify-end gap-8 text-[#574021]">
                  <span>Shipping:</span>
                  <span>Rs. {order.shipping_cost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between md:justify-end gap-8 font-bold text-[#2C1E0F] text-lg pt-2 border-t border-[#6B4F2A]/10">
                  <span>Total:</span>
                  <span data-testid="order-total">Rs. {order.total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-[#1F7A63]/5 rounded-2xl p-6 mb-8">
          <h3 className="font-bold text-[#2C1E0F] mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#1F7A63]" />
            What's Next?
          </h3>
          <ul className="space-y-3 text-[#574021]">
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#1F7A63] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span>You'll receive a confirmation email with your order details.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#1F7A63] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span>We'll process your order within 24 hours.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 bg-[#1F7A63] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span>Your order will be delivered within 3-5 business days.</span>
            </li>
            {order.payment_method === 'jazzcash' && (
              <li className="flex items-start gap-2">
                <span className="w-6 h-6 bg-[#E0A106] text-[#2C1E0F] rounded-full flex items-center justify-center text-xs flex-shrink-0">!</span>
                <span>Please complete your JazzCash payment. Details will be sent via WhatsApp.</span>
              </li>
            )}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop" className="btn-primary text-center">
            Continue Shopping
          </Link>
          <Link to="/contact" className="btn-outline text-center">
            Need Help?
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
